import withRole from "../../Hooks/withRole";
import genericStyles from "../../Styles/genericStyles.module.css";
import MUIDataTableCustomStyle from "../../Components/MUIDataTableCustomStyle";
import { Vocabulary } from "../../Utils/Vocabulary";
import Filters from "../Widgets/Filters";
import { EComponentTypes, ETableEditAction } from "../../Utils/Enums";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import * as yup from "yup";
import Config from "../../Utils/Config";
import { Filter, PaymentFormType } from "../../Utils/Types";
import { formatDate, renderTableLink } from "../../Utils/Utils";
import { FormsIds, PaymentMethods, PaymentType } from "../../Utils/Constants";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { Chip, TableCell, TableRow, Typography } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ModalButton from "../ModalButton";
import PaymentForm from "./PaymentForm";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AccountSchema, ClientModel, objectOrId } from "../../Utils/Models";
import { getData } from "../../Services/getData";
import { AxiosResponse } from "axios";
import WithdrawMoneyForm from "./WithdrawMoneyForm";
import useCustomMemo from "../../Hooks/useCustomMemo";
import SellBookButton from "../Books/SellBookButton";

function CashDeskTable(props: any) {
  const [clients, setClients] = useState([] as ClientModel[]);
  const [triggerReload, setTriggerReload] = useState<undefined | boolean>();
  const [officeAssistants, setOfficeAssistants] = useState([] as any[]);
  const [searchParams] = useSearchParams();
  const memo = useCustomMemo();
  const bankAccounts = memo?.storage.bankAccounts;
  const bankAccountId = props?.restrictByWorkPoint
    ? localStorage.getItem("bankAccountId")
    : undefined;
  const url = localUrlEnum.order;
  const selectedPaymentMethod = searchParams.get("paymentMethod");
  const isCashMethod =
    !selectedPaymentMethod || selectedPaymentMethod === PaymentMethods.CASH;

  useEffect(() => {
    getData(`${endpoints.client.main}`).then((res: AxiosResponse) => {
      if (res?.data?.length > 0) setClients(res?.data);
    });
    getData(`${endpoints.user.role}/${Config.roles.OFFICE_ASSISTANT}`).then(
      (res: AxiosResponse) => {
        if (res?.data?.length > 0) setOfficeAssistants(res?.data);
      },
    );
  }, []);



  /**
   * Columns for the table to display
   */
  const columns = [
    {
      name: "order_id",
      label: "",
      options: {
        filter: false,
        display: false,
      },
    },
    {
      name: "incrementId",
      label: Vocabulary.nr,
      options: {
        filter: false,
        customBodyRender: (rowData: any, tableMeta: any) => {
          return renderTableLink(url, rowData, tableMeta);
        },
      },
    },
    {
      name: "paymentType",
      label: Vocabulary.type,
      options: {
        filter: false,
        customBodyRender: (rowData: any, tableMeta: any) => {
          return renderTableLink(url, rowData, tableMeta);
        },
      },
    },
    {
      name: "paymentMethod",
      label: Vocabulary.method,
      options: {
        filter: false,
        customBodyRender: (rowData: any, tableMeta: any) => {
          return renderTableLink(url, rowData, tableMeta);
        },
      },
    },
    {
      name: "accountName",
      label: Vocabulary.account,
      options: {
        filter: false,
        customBodyRender: (rowData: any, tableMeta: any) => {
          return renderTableLink(url, rowData, tableMeta);
        },
      },
    },
    {
      name: "amount",
      label: Vocabulary.amount,
      options: {
        filter: false,
        customBodyRender: (rowData: any, tableMeta: any) => {
          return renderTableLink(url, rowData, tableMeta);
        },
      },
    },
    {
      name: "clientName",
      label: Vocabulary.client,
      options: {
        filter: false,
        customBodyRender: (rowData: any, tableMeta: any) => {
          return renderTableLink(url, rowData, tableMeta);
        },
      },
    },
    {
      name: "instructorName",
      label: Vocabulary.instructor,
      options: {
        filter: false,

        customBodyRender: (rowData: any) => {
          if (!rowData) {
            return null;
          }
          return (
            <Chip
              icon={<AccountCircleIcon />}
              color={"default"}
              label={rowData}
              style={{ padding: 5 }}
            />
          );
        },
      },
    },
    {
      name: "employeeName",
      label: Vocabulary.employee,
      options: {
        filter: false,
        customBodyRender: (rowData: any) => {
          if (!rowData) {
            return null;
          }
          return (
            <Chip
              icon={<AccountCircleIcon />}
              color={"default"}
              label={rowData}
              style={{ padding: 5 }}
            />
          );
        },
      },
    },
    {
      name: "vehicleName",
      label: Vocabulary.vehicle,
      options: {
        filter: false,
        customBodyRender: (rowData: any, tableMeta: any) => {
          return renderTableLink(url, rowData, tableMeta);
        },
      },
    },
    {
      name: "description",
      label: Vocabulary.description,
      options: {
        filter: false,
        customBodyRender: (rowData: any, tableMeta: any) => {
          return renderTableLink(url, rowData, tableMeta);
        },
      },
    },
    {
      name: "createdAt",
      label: Vocabulary.createdAt,
      options: {
        filter: false,
        customBodyRender: (date: Date) => {
          return <>{formatDate(date)}</>;
        },
      },
    },
    {
      name: "createdByUsername",
      label: Vocabulary.createdBy,
    },
  ];

  /**
   * Filters for the table
   */
  const filters: Filter[] = [
    {
      property: "start",
      label: Vocabulary.startDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: Config.defaultEndDate,
      clearable: false,
    },
    {
      property: "end",
      label: Vocabulary.endDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: Config.defaultEndDate,
      clearable: false,
    },
    {
      property: "accountId",
      autocompleteFilterProperty: "_id",
      label: Vocabulary.account,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: objectOrId(AccountSchema, false),
      defaultValue: bankAccountId || null,
      options: bankAccounts,
      disableFlag: props?.restrictByWorkPoint,
    },
    {
      property: "paymentType",
      autocompleteFilterProperty: "paymentType",
      label: Vocabulary.type,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.string().nullable(),
      defaultValue: null,
      options: props?.paymentTypes || [
        Vocabulary.amount,
        Vocabulary.income,
        PaymentType.WITHDRAW,
      ],
    },
    {
      property: "paymentMethod",
      autocompleteFilterProperty: "paymentMethod",
      label: Vocabulary.paymentMethod,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.string().nullable(),
      defaultValue: null,
      options: props?.paymentMethods || Object.values(PaymentMethods),
    },
  ];
  /**
   * @function
   * @description Generates a row of buttons for a cash desk table
   * @returns {JSX.Element} A row of buttons
   */
  const tableButtons = () => {
    return (
      <div className={genericStyles.cashDeskButtons}>
        <ModalButton
          buttonTitle={Vocabulary.payments}
          content={
            <PaymentForm
              paymentType={PaymentFormType.PAYMENT}
              clients={clients}
              onClose={() => setTriggerReload(!triggerReload)}
              showPaymentType={true}
              name="PaymentForm"
            />
          }
          buttonIcon={<MonetizationOnIcon />}
          buttonSize="large"
          buttonColor="primary"
          formId={FormsIds.salaryForm}
        />
        <ModalButton
          buttonTitle={Vocabulary.salaries}
          content={
            <PaymentForm
              paymentType={PaymentFormType.SALARY}
              clients={clients}
              onClose={() => setTriggerReload(!triggerReload)}
              showPaymentType={true}
              officeAssistants={officeAssistants}
              name="PaymentFormSalary"
            />
          }
          buttonIcon={<MonetizationOnIcon />}
          buttonSize="large"
          buttonColor="primary"
          formId={FormsIds.salaryForm}
        />
        {props?.showWithdrawButton && (
          <ModalButton
            buttonTitle={Vocabulary.moneyWithdraw}
            content={
              <WithdrawMoneyForm
                onClose={() => setTriggerReload(!triggerReload)}
              />
            }
            buttonIcon={<MonetizationOnIcon />}
            buttonSize="large"
            buttonColor="error"
            formId={FormsIds.withdrawMoneyForm}
          />
        )}
        {clients?.length > 0 && (
          <SellBookButton
            name="SellBookButton"
            onClose={() => setTriggerReload(!triggerReload)}
            clients={clients}
          />
        )}
      </div>
    );
  };

  /**
   * Generates footer fields array from API response
   */
  const getFooterFields = (footerFields: any) => {
    const fields = [];
    if (isCashMethod) {
      fields.push({
        label: Vocabulary.previousBalance,
        value: footerFields?.previousBalance || 0,
        color: "green",
      });
    }
    fields.push(
      {
        label: Vocabulary.totalIncome,
        value: footerFields?.totalIncome || 0,
        color: "green",
      },
      {
        label: Vocabulary.totalPayments,
        value: footerFields?.totalPayment || 0,
        color: "red",
      },
      {
        label: Vocabulary.balance,
        value:
          (isCashMethod ? footerFields?.previousBalance || 0 : 0) +
          (footerFields?.totalIncome || 0) +
          (footerFields?.totalPayment || 0),
        color: "black",
      },
    );
    return fields;
  };

  /**
   * Returns footer fields formatted for export (print/download)
   */
  const getExportFooterFields = (footerFields: any) =>
    getFooterFields(footerFields).map((field) => ({
      ...field,
      value: `${field.value} ${Vocabulary.currency}`,
    }));

  /**
   * Returns a JSX element representing the custom footer row.
   */
  const renderCustomFooter = (footerFields: any) => {
    const fields = getFooterFields(footerFields);

    return (
      <>
        {fields?.map((field: any, index: number) => (
          <TableRow key={index}>
            <TableCell colSpan={2}>
              <Typography style={{ color: field.color, width: "100%" }}>
                <strong>{field.label}:</strong>
              </Typography>
            </TableCell>
            <TableCell colSpan={11}>
              <Typography style={{ color: "black", width: "100%" }}>
                <strong>{`${field.value} ${Vocabulary.currency}`}</strong>
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  /**
   * @returns {JSX.Element}
   */
  return (
    <div className={genericStyles.mainContainer}>
      {tableButtons()}
      <div className={genericStyles.secondContainer}>
        <Filters filters={filters} defaultOpen={true} filterWidth={12} />

        <MUIDataTableCustomStyle
          onEditAction={ETableEditAction.NONE}
          title={Vocabulary.cashDesk}
          columns={columns}
          CRUDEndPoint={endpoints.payments.main}
          hideAddButton
          hideEditButton
          hideDeleteButton={!props?.showDeleteButton}
          customFooter={renderCustomFooter}
          triggerReload={triggerReload}
          showPrintButton
          showDownloadButton
          exportFooterFields={getExportFooterFields}
          filters={{
            ...(props?.paymentMethods && {
              paymentMethod: props.paymentMethods.join(","),
            }),
            ...(props?.paymentTypes && {
              paymentType: props.paymentTypes.join(","),
            }),
          }}
        />
      </div>
    </div>
  );
}

export default withRole(CashDeskTable);
